import React, { useEffect, useState, useRef } from 'react';

// Format time in MM:SS format
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Helper to set theme color for Android status bar
const setThemeColour = (clr) => {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = clr;
};

export default function AudioStatusWidget({
  status,
  title,
  mode,
  onPauseToggle,
  onStop,
  currentTime = 0,
  duration = 0,
  onSeek,
  onTogglePlayPause,
  embedded = false,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [localTime, setLocalTime] = useState(currentTime);
  const sliderRef = useRef(null);

  // Set theme color when component mounts
  useEffect(() => {
    setThemeColour('#1f2937');
    // Cleanup function to reset theme color when component unmounts
    return () => setThemeColour('#1f2937'); // Reset to slate blue
  }, []);

  // Update local time when currentTime changes (but not while dragging)
  useEffect(() => {
    if (!isDragging) {
      setLocalTime(currentTime);
    }
  }, [currentTime, isDragging]);

  // Keyboard Esc = stop (accessibility)
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onStop();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onStop]);

  // Handle global mouse events for dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleSliderMouseMove(e);
      const handleGlobalMouseUp = (e) => handleSliderMouseUp(e);

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, duration, onSeek]);

  // Only visible while any audio activity
  if (status === 'idle') return null;

  // Label: include a mode label for clarity
  let label;
  if (mode === 'historyOverview') {
    label = `${title} history overview`;
  } else if (mode === 'anecdote') {
    label = `${title} anecdote`;
  } else if (mode === 'periods' || mode === 'historicPeriod' || mode === 'understandCard') {
    label = `${title}`;
  } else {
    label = `${title} overview`;
  }

  // Calculate progress percentage
  const progress = duration > 0 ? (localTime / duration) * 100 : 0;

  // Handle slider interaction
  const handleSliderMouseDown = (e) => {
    if (duration <= 0) return;
    setIsDragging(true);
    updateSliderFromEvent(e);
  };

  const handleSliderMouseMove = (e) => {
    if (!isDragging || duration <= 0) return;
    updateSliderFromEvent(e);
  };

  const handleSliderMouseUp = (e) => {
    if (!isDragging || duration <= 0) return;
    setIsDragging(false);
    updateSliderFromEvent(e);
    // Actually seek to the position
    if (onSeek) {
      onSeek(localTime);
    }
  };

  const updateSliderFromEvent = (e) => {
    if (!sliderRef.current || duration <= 0) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const newTime = (percentage / 100) * duration;
    setLocalTime(newTime);
  };

  // Styles
  const styles = {
    container: {
      position: embedded ? 'relative' : 'fixed',
      top: embedded ? 'auto' : 10,
      left: embedded ? 'auto' : '50%',
      transform: embedded ? 'none' : 'translateX(-50%)',
      width: embedded ? '100%' : 'auto',
      background: '#1f2937',
      color: 'white',
      padding: embedded
        ? `10px 12px calc(10px + env(safe-area-inset-bottom, 0px)) 12px`
        : '12px 16px',
      borderRadius: embedded ? 0 : 12,
      minWidth: embedded ? 'unset' : '320px',
      maxWidth: embedded ? '100%' : '480px',
      zIndex: embedded ? 'auto' : 1000,
      fontSize: 14,
      boxSizing: 'border-box',
      border: embedded
        ? '1px solid rgba(255, 255, 255, 0.08)'
        : '1px solid rgba(255, 255, 255, 0.08)',
      borderLeft: embedded ? 'none' : undefined,
      borderRight: embedded ? 'none' : undefined,
      borderBottom: embedded ? 'none' : undefined,
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      boxShadow: embedded ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.3)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    title: {
      fontWeight: 500,
      fontSize: 13,
      flex: 1,
      textAlign: 'center',
    },
    controls: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    playButton: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      fontSize: 20,
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background 0.2s',
    },
    stopButton: {
      background: 'transparent',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      fontSize: 16,
      padding: '4px 8px',
      borderRadius: 4,
      transition: 'background 0.2s',
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 8,
      flex: 1,
    },
    timeDisplay: {
      fontSize: 11,
      minWidth: 35,
      textAlign: 'center',
    },
    sliderContainer: {
      flex: 1,
      height: 20,
      display: 'flex',
      alignItems: 'center',
      cursor: duration > 0 ? 'pointer' : 'default',
      padding: '0 8px', // Add some padding for easier clicking
    },
    sliderTrack: {
      width: '100%',
      height: 6, // Increased height for better visibility
      background: 'rgba(255, 255, 255, 0.8)', // Much more visible background
      borderRadius: 3,
      position: 'relative',
      overflow: 'hidden',
    },
    sliderProgress: {
      height: '100%',
      background: duration > 0 ? '#1976d2' : 'rgba(255, 255, 255, 0.6)',
      borderRadius: 3,
      transition: isDragging ? 'none' : 'width 0.1s',
      width: `${progress}%`,
    },
    sliderThumb: {
      position: 'absolute',
      top: '50%',
      left: `${progress}%`,
      transform: 'translate(-50%, -50%)',
      width: 14, // Slightly larger thumb
      height: 14,
      background: duration > 0 ? '#1976d2' : 'rgba(255, 255, 255, 0.9)',
      borderRadius: '50%',
      opacity: 1, // Always fully visible
      transition: isDragging ? 'none' : 'left 0.1s',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)', // Add shadow for better visibility
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>{label}</div>
        <button
          style={styles.stopButton}
          onClick={onStop}
          onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          onMouseOut={(e) => e.target.style.background = 'transparent'}
          aria-label="stop"
        >
          ✕
        </button>
      </div>

      <div style={styles.controls}>
        <button
          style={styles.playButton}
          onClick={onTogglePlayPause || onPauseToggle}
          onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          aria-label="play/pause"
        >
          {status === 'playing' ? '⏸' : '▶️'}
        </button>

        <div style={styles.progressContainer}>
          <div style={styles.timeDisplay}>
            {formatTime(localTime)}
          </div>

          <div
            ref={sliderRef}
            style={styles.sliderContainer}
            onMouseDown={handleSliderMouseDown}
          >
            <div style={styles.sliderTrack}>
              <div style={styles.sliderProgress} />
              <div style={styles.sliderThumb} />
            </div>
          </div>

          <div style={styles.timeDisplay}>
            {formatTime(duration)}
          </div>
        </div>
      </div>
    </div>
  );
}
